export const featuresFlaws = [
  {
    slug: 'FILTERING_IN_ARGUMENTS',
    meaning: 'Provider filter DSL parameter (`filter`, `filters`, `where`, `filter_by`, …) exposed as an ordinary argument while no filter feature is active.',
    detectionModes: ['classifier'],
    severity: 'medium',
    fixCategories: ['filtering'],
    repairPolicy: 'verified_review',
    verificationBaseMustSucceed: false,
    requiredEvidence: {
      observationIds: {
        type: 'stringArray',
        minItems: 1
      },
      argumentKeys: {
        type: 'stringArray',
        minItems: 1
      },
      filterFeatureActive: {
        type: 'boolean',
        const: false
      },
      providerDslConfirmed: {
        type: 'boolean',
        const: true
      },
      carrierExceptionApplies: {
        type: 'boolean',
        const: false
      }
    },
    evidenceAlternatives: [],
    verificationRecipe: ['API filters active with location/operators; action fields configured; raw arg removed or kept per carrier exception; a filtered run pushes to the provider.'],
    legacyCodes: [],
    evidenceRelations: []
  },
  {
    slug: 'FILTERING_ISSUE',
    meaning: 'Provider filtering is missing or produces incorrect requests or results.',
    detectionModes: ['classifier', 'code'],
    severity: 'high',
    fixCategories: ['filtering'],
    repairPolicy: 'verified_review',
    verificationBaseMustSucceed: false,
    requiredEvidence: {
      observationIds: {
        type: 'stringArray',
        minItems: 1
      }
    },
    evidenceAlternatives: [
      {
        filterFeatureExposed: {
          type: 'boolean',
          const: false
        },
        providerFilterParameter: {
          type: 'string'
        },
        providerDocumentationUrl: {
          type: 'string'
        }
      },
      {
        filterFeatureExposed: {
          type: 'boolean',
          const: false
        },
        providerFilterParameter: {
          type: 'string'
        },
        providerFilteringObserved: {
          type: 'boolean',
          const: true
        }
      },
      {
        filterFeatureExposed: {
          type: 'boolean',
          const: false
        },
        apiFiltersActive: {
          type: 'boolean',
          const: true
        },
        actionsExposingFilters: {
          type: 'integer',
          const: 0
        }
      },
      {
        requestNoticeCode: {
          type: 'string',
          const: 'FILTER_ENTRY_DROPPED'
        },
        declaredFilterDropped: {
          type: 'boolean',
          const: true
        }
      },
      {
        configPaths: {
          type: 'stringArray',
          minItems: 1
        },
        invalidFilterSource: {
          type: 'boolean',
          const: true
        }
      },
      {
        configPaths: {
          type: 'stringArray',
          minItems: 1
        },
        filteredRunObserved: {
          type: 'boolean',
          const: true
        },
        providerPushdownFailed: {
          type: 'boolean',
          const: true
        }
      }
    ],
    verificationRecipe: ['API filters active; action filters enabled with a valid field source; filtered run observed.', 'operator set explicit and mapped; location pushable; field source valid; filtered run pushed by provider.'],
    legacyCodes: [],
    evidenceRelations: []
  },
  {
    slug: 'SORTING_IN_ARGUMENTS',
    meaning: 'Provider sort parameter (`sort`, `sortBy`, `orderBy`, `sortOrder`, …) exposed as an ordinary argument while no sort feature is active.',
    detectionModes: ['classifier'],
    severity: 'medium',
    fixCategories: ['sorting'],
    repairPolicy: 'verified_review',
    verificationBaseMustSucceed: false,
    requiredEvidence: {
      observationIds: {
        type: 'stringArray',
        minItems: 1
      },
      argumentKeys: {
        type: 'stringArray',
        minItems: 1
      },
      sortFeatureActive: {
        type: 'boolean',
        const: false
      },
      providerSortParameter: {
        type: 'string'
      }
    },
    evidenceAlternatives: [],
    verificationRecipe: ['API sort active with externalKey/location; action sort enabled; sort applied by the provider.'],
    legacyCodes: [],
    evidenceRelations: []
  },
  {
    slug: 'SORTING_ISSUE',
    meaning: 'Provider sorting is missing or produces incorrect requests or results.',
    detectionModes: ['code', 'classifier'],
    severity: 'medium',
    fixCategories: ['sorting'],
    repairPolicy: 'verified_review',
    verificationBaseMustSucceed: false,
    requiredEvidence: {
      observationIds: {
        type: 'stringArray',
        minItems: 1
      }
    },
    evidenceAlternatives: [
      {
        apiSortActive: {
          type: 'boolean',
          const: true
        },
        actionsExposingSort: {
          type: 'integer',
          const: 0
        }
      },
      {
        requestNoticeCode: {
          type: 'string',
          const: 'SORT_SKIPPED_API_INACTIVE'
        },
        requestedSortDropped: {
          type: 'boolean',
          const: true
        }
      },
      {
        configPaths: {
          type: 'stringArray',
          minItems: 1
        },
        sortedRunObserved: {
          type: 'boolean',
          const: true
        },
        providerSortDropped: {
          type: 'boolean',
          const: true
        }
      }
    ],
    verificationRecipe: ['action sort enabled; sorted run observed.', 'API sort config complete; action sort effective; sorted run observed.'],
    legacyCodes: [],
    evidenceRelations: []
  },
  {
    slug: 'RETRY_ISSUE',
    meaning: 'Retry behavior is missing or violates the runtime retry contract.',
    detectionModes: ['classifier', 'code'],
    severity: 'medium',
    fixCategories: ['executionContract'],
    repairPolicy: 'verified_review',
    verificationBaseMustSucceed: false,
    requiredEvidence: {
      observationIds: {
        type: 'stringArray',
        minItems: 1
      }
    },
    evidenceAlternatives: [
      {
        retryActive: {
          type: 'boolean',
          const: false
        },
        transientFailureObservationIds: {
          type: 'stringArray',
          minItems: 2
        }
      },
      {
        configPaths: {
          type: 'stringArray',
          minItems: 1
        },
        retryBehaviorObserved: {
          type: 'boolean',
          const: true
        },
        retryContractViolation: {
          type: 'string'
        }
      }
    ],
    verificationRecipe: ['retry active; a transient failure run retried and succeeded.', 'status-code list explicit and safe; attempts within cap; a retry observed.'],
    legacyCodes: [],
    evidenceRelations: [
      {
        left: 'transientFailureObservationIds',
        operator: 'subset',
        right: 'observationIds'
      }
    ],
    codeEvidenceAlternatives: [
      {
        configPaths: {
          type: 'stringArray',
          minItems: 1
        },
        retryBehaviorObserved: {
          type: 'boolean',
          const: true
        },
        retryContractViolation: {
          type: 'string'
        }
      }
    ]
  }
];
